import { defineStore } from 'pinia';

export type PresenceUser = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  has_profile_picture: boolean;
};

export type CursorPosition = {
  x: number;
  wsId: string;
  wsRatio: number;
};

export type EntitySelection = {
  entityType: string;
  entityId: string;
};

export type MarkupPoint = {
  x: number;
  wsId: string;
  wsRatio: number;
};

export type MarkupStroke = {
  points: MarkupPoint[];
};

const PRESENCE_COLORS = [
  '#3B82F6', // blue
  '#10B981', // emerald
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F97316', // orange
];

type PresenceStoreState = {
  activeUsers: Record<string, PresenceUser>;
  cursors: Record<string, CursorPosition>;
  selections: Record<string, EntitySelection>;
  showLiveCursors: boolean;
  followingUserId: string;
  localMarkupStrokes: MarkupStroke[];
  remoteMarkupStrokes: Record<string, MarkupStroke[]>;
  currentDrawingPoints: MarkupPoint[];
  followers: string[];
  _userColorMap: Record<string, string>;
  _nextColorIndex: number;
  _pendingRollCall: Record<string, PresenceUser>;
  _rollCallTimer: ReturnType<typeof setTimeout> | null;
};

const ROLL_CALL_TIMEOUT_MS = 2000;
const LIVE_CURSORS_STORAGE_KEY = 'beeboard-show-live-cursors';

export const usePresenceStore = defineStore('presenceStore', {
  state: (): PresenceStoreState => ({
    activeUsers: {},
    cursors: {},
    selections: {},
    showLiveCursors: localStorage.getItem(LIVE_CURSORS_STORAGE_KEY) !== 'false',
    followingUserId: '',
    localMarkupStrokes: [],
    remoteMarkupStrokes: {},
    currentDrawingPoints: [],
    followers: [],
    _userColorMap: {},
    _nextColorIndex: 0,
    _pendingRollCall: {},
    _rollCallTimer: null,
  }),

  getters: {
    users: (state): PresenceUser[] => Object.values(state.activeUsers),

    userColor:
      (state) =>
      (userId: string): string => {
        return state._userColorMap[userId] ?? PRESENCE_COLORS[0];
      },

    isFollowing: (state): boolean => !!state.followingUserId,

    followedUser: (state): PresenceUser | null =>
      state.followingUserId
        ? (state.activeUsers[state.followingUserId] ?? null)
        : null,

    isBeingFollowed: (state): boolean => state.followers.length > 0,
  },

  actions: {
    handleRollCall() {
      this._pendingRollCall = {};

      if (this._rollCallTimer) {
        clearTimeout(this._rollCallTimer);
      }

      this._rollCallTimer = setTimeout(() => {
        const previousUsers = Object.keys(this.activeUsers);
        this.activeUsers = { ...this._pendingRollCall };

        // Clean up cursors and selections for users who left
        for (const userId of previousUsers) {
          if (!this.activeUsers[userId]) {
            delete this.cursors[userId];
            delete this.selections[userId];
            delete this.remoteMarkupStrokes[userId];
            this.followers = this.followers.filter((id) => id !== userId);
            // Stop following if the followed user left
            if (this.followingUserId === userId) {
              this.followingUserId = '';
            }
          }
        }

        this._rollCallTimer = null;
      }, ROLL_CALL_TIMEOUT_MS);
    },

    handleAnnounce(user: PresenceUser) {
      this.activeUsers[user.id] = user;
      this._pendingRollCall[user.id] = user;
      this._assignColor(user.id);
    },

    updateCursor(userId: string, x: number, wsId: string, wsRatio: number) {
      this.cursors[userId] = { x, wsId, wsRatio };
    },

    updateSelection(userId: string, entityType: string, entityId: string) {
      if (!entityType || !entityId) {
        delete this.selections[userId];
      } else {
        this.selections[userId] = { entityType, entityId };
      }
    },

    followUser(userId: string) {
      // Can't follow if someone is already following us
      if (this.followers.length > 0) {
        return;
      }
      this.followingUserId = userId;
    },

    unfollowUser() {
      this.followingUserId = '';
      // Clean up remote markup when we stop following
      this.remoteMarkupStrokes = {};
    },

    addFollower(userId: string) {
      if (!this.followers.includes(userId)) {
        this.followers.push(userId);
      }
    },

    removeFollower(userId: string) {
      this.followers = this.followers.filter((id) => id !== userId);
      // Clean up own markup when no one is following anymore
      if (this.followers.length === 0) {
        this.localMarkupStrokes = [];
        this.currentDrawingPoints = [];
      }
    },

    addLocalMarkupStroke(stroke: MarkupStroke) {
      this.localMarkupStrokes.push(stroke);
    },

    clearLocalMarkup() {
      this.localMarkupStrokes = [];
    },

    addRemoteMarkupStroke(userId: string, points: MarkupPoint[]) {
      if (!this.remoteMarkupStrokes[userId]) {
        this.remoteMarkupStrokes[userId] = [];
      }
      this.remoteMarkupStrokes[userId].push({ points });
    },

    clearRemoteMarkup(userId: string) {
      delete this.remoteMarkupStrokes[userId];
    },

    toggleLiveCursors() {
      this.showLiveCursors = !this.showLiveCursors;
      localStorage.setItem(
        LIVE_CURSORS_STORAGE_KEY,
        String(this.showLiveCursors),
      );
    },

    _assignColor(userId: string) {
      if (this._userColorMap[userId]) {
        return;
      }
      this._userColorMap[userId] =
        PRESENCE_COLORS[this._nextColorIndex % PRESENCE_COLORS.length];
      this._nextColorIndex++;
    },

    reset() {
      if (this._rollCallTimer) {
        clearTimeout(this._rollCallTimer);
      }
      this.activeUsers = {};
      this.cursors = {};
      this.selections = {};
      this.followingUserId = '';
      this.localMarkupStrokes = [];
      this.remoteMarkupStrokes = {};
      this.currentDrawingPoints = [];
      this.followers = [];
      this._userColorMap = {};
      this._nextColorIndex = 0;
      this._pendingRollCall = {};
      this._rollCallTimer = null;
    },
  },
});
